import useUtils from 'src/composables/utils';
import useValidationUtils from 'src/composables/validationUtils';
import useDateHelperUtils from 'src/composables/dateHelperUtils';
import useRateHelperUtils from 'src/composables/rateHelperUtils.ts';
import {
  PreCheckInGuestDTO,
  PreCheckInBookerDTO,
  PreCheckInReservationEditDTO,
  CreditCardDTO,
  PreCheckInReservationItemViewDTO,
  PolicyDTO,
  PreCheckInAccountDTO,
  PreCheckInReservationItemEditDTO,
  SavePreCheckInRequestDTO,
  PreCheckInConfigurationDTO,
  ApiDropdown,
  PreCheckInReservationItemServiceDTO,
} from 'src/services/api/data-contracts';
import { ReservationItemService } from 'src/types/common';

const dateHelperUtils = useDateHelperUtils();
const rateHelperUtils = useRateHelperUtils();
const utils = useUtils();
const validationUtils = useValidationUtils();

export enum PreCheckinProcessStep {
  None = 0,
  Start = 1,
  Booker = 2,
  Room = 3,
  Guest = 4,
  Extras = 5,
  PaymentDetails = 6,
  Summary = 7,
  Complete = 8,
}

export class InvalidPreCheckInResponse {
  constructor() {
    this.BadgeColour;
  }
  BadgeColour: string | null;
  BadgeMessage: string | null;
  Message: string | null;
}

export class PreCheckInReservation {
  constructor(
    data: PreCheckInReservationEditDTO,
    roomConfigurationIsRequired: boolean,
    config: PreCheckInConfigurationDTO,
  ) {
    Object.assign(this, data);

    this.PropertyId = data.PropertyId;
    this.ReferenceNumber = data.ReferenceNumber;
    this.IsReservation = data.IsReservation;
    this.EntityId = data.EntityId;
    this.SpecialRequests = data.SpecialRequests;
    this.TotalAccommodationPrice = data.TotalAccommodationPrice;
    this.TotalServicesPrice = data.TotalServicesPrice;
    this.NumberOfAdults = data.NumberOfAdults;
    this.NumberOfChildren = data.NumberOfChildren;
    this.NumberOfInfants = data.NumberOfInfants;
    this.NumberOfAdults = data.NumberOfAdults;
    this.NumberOfChildren = data.NumberOfChildren;
    this.NumberOfInfants = data.NumberOfInfants;

    this.CreditCards = data.CreditCards;
    this.Booker =
      data.Booker != null ? new PreCheckInBooker(data.Booker, config) : null;
    this.Company = data.Company;

    this.ReservationItems = data.ReservationItems.map((g) => {
      return new PreCheckInReservationItem(
        g,
        roomConfigurationIsRequired,
        config,
      );
    });
  }

  PropertyId: number;
  ReferenceNumber: string;
  IsReservation: boolean;
  EntityId: number;
  SpecialRequests: string;
  TotalAccommodationPrice: number;
  TotalServicesPrice: number;

  NumberOfAdults: number;
  NumberOfChildren: number;
  NumberOfInfants: number;

  CreditCards: CreditCardDTO[];
  Booker?: PreCheckInBooker;
  Company?: PreCheckInAccountDTO;
  ReservationItems: PreCheckInReservationItem[];

  get welcomeName(): string {
    if (this.IsReservation) {
      const b = this.Booker;
      if (b != null) {
        return b.FirstName;
      } else {
        return 'Guest';
      }
    } else {
      const firstRes = this.ReservationItems[0];
      if (firstRes.Guests.length > 0) {
        const leadGuest = firstRes.Guests[0];
        return leadGuest.FirstName;
      }
    }
    return '';
  }

  get leadGuestFullName(): string {
    if (this.IsReservation) {
      const b = this.Booker;
      if (b != null) {
        return b.FirstName + ' ' + b.LastName;
      } else if (this.Company != null) {
        return this.Company.Name;
      }
    } else {
      const firstRes = this.ReservationItems[0];
      if (firstRes.Guests.length > 0) {
        const leadGuest = firstRes.Guests[0];
        return leadGuest.FirstName + ' ' + leadGuest.LastName;
      }
    }
    return '';
  }

  updateGuestsWithBooker() {
    const bookerGuests = this.ReservationItems.flatMap(
      (reservation) => reservation.Guests,
    ).filter((guest) => guest.IndividualId === this.Booker.IndividualId);

    if (bookerGuests.length > 0) {
      bookerGuests.forEach((bg) => {
        bg.FirstName = this.Booker.FirstName;
        bg.LastName = this.Booker.LastName;
        bg.Email = this.Booker.Email;
        bg.Phone = this.Booker.Phone;

        bg.haveReviewedGuest = true;
      });
    }
  }

  toDTO(hash: string): SavePreCheckInRequestDTO {
    return {
      PropertyId: this.PropertyId,
      ReferenceNumber: this.ReferenceNumber,
      IsReservation: this.IsReservation,
      EntityId: this.EntityId,
      Code: hash,
      SpecialRequests: this.SpecialRequests,
      Booker: this.Booker != null ? this.Booker.toDTO() : null,
      ReservationItems: this.ReservationItems.map((ri) => {
        return ri.toDTO();
      }),
    } as SavePreCheckInRequestDTO;
  }
}

export class PreCheckInReservationItem {
  constructor(
    data: PreCheckInReservationItemViewDTO,
    roomConfigurationIsRequired: boolean,
    config: PreCheckInConfigurationDTO,
  ) {
    this.ReservationItemId = data.ReservationItemId;
    this.DateOfArrival = data.DateOfArrival;
    this.DateOfDeparture = data.DateOfDeparture;
    this.RoomTypeId = data.RoomTypeId;
    this.RoomType = data.RoomType;
    this.RatePlanId = data.RatePlanId;
    this.RatePlan = data.RatePlan;
    this.Policy = data.Policy;
    this.RoomConfiguration = data.RoomConfiguration;
    this.RoomConfigurationIsRequired = roomConfigurationIsRequired;
    this.NumberOfAdults = data.NumberOfAdults;
    this.NumberOfChildren = data.NumberOfChildren;
    this.NumberOfInfants = data.NumberOfInfants;

    this.CreditCards = data.CreditCards;
    this.Guests = data.Guests.map((g) => {
      return new PreCheckInGuest(g, config);
    });
    this.Services = data.Services.map((g) => {
      return new ReservationItemService(g);
    });

    this.haveReviewedRoom = false;

    this.haveSeenServices = false;
    this.areServicesLoaded = false;
    this.hasSelectableServices = false;
  }

  ReservationItemId: number;

  haveReviewedRoom: boolean;
  get haveReviewedAllGuests(): boolean {
    return this.Guests.reduce((t, y) => {
      return t && y.haveReviewedGuest && y.isValid;
    }, true);
  }

  DateOfArrival: string;
  DateOfDeparture: string;
  RoomTypeId: number;
  RoomType: string;
  RatePlanId: number;
  RatePlan: string;
  Policy?: PolicyDTO;

  RoomConfigurationIsRequired: boolean;
  RoomConfiguration?: ApiDropdown;

  NumberOfAdults: number;
  NumberOfChildren: number;
  NumberOfInfants: number;

  CreditCards: CreditCardDTO[];
  Guests: PreCheckInGuest[];

  get isValid(): boolean {
    return (
      this.guestsAreValid &&
      (!this.RoomConfigurationIsRequired ||
        (this.RoomConfigurationIsRequired && this.RoomConfiguration != null))
    );
  }

  get roomConfigurationIsValid(): boolean {
    return (
      !this.RoomConfigurationIsRequired ||
      (this.RoomConfigurationIsRequired && this.RoomConfiguration != null)
    );
  }

  get guestsAreValid(): boolean {
    const v = this.Guests.reduce((v, g) => {
      return v && g.isValid;
    }, true);
    return v;
  }

  Services: ReservationItemService[];

  activeGuestNumber(guest: PreCheckInGuest): number {
    let index = -2;

    index = this.Guests.findIndex((u) => u == guest);
    index = index + 1;

    return index;
  }

  /// Find a reservation item based on Room Id, with an optional selector
  getGuest(
    guestId: string,
    selector: (ri: PreCheckInGuest) => boolean = () => {
      return true;
    },
  ): PreCheckInGuest | null {
    const items = this.Guests.filter((y) => {
      return y.guestId == guestId && selector(y);
    });
    if (items.length > 0) {
      return items[0];
    }
    return null;
  }

  getNextGuest(
    currentGuest: PreCheckInGuest,
    selector: (ri: PreCheckInGuest) => boolean,
  ): PreCheckInGuest | null {
    let nextGuest: PreCheckInGuest = null;
    let index = -1;

    const getNextGuest = (index: number) => {
      let nextX = this.Guests[index];
      if (nextX != null) {
        const resX = this.getGuest(nextX.guestId, selector);
        if (resX == null) {
          nextX = getNextGuest(index + 1);
        } else {
          nextX = resX;
        }
      }

      return nextX;
    };

    if (currentGuest == null) {
      index = 0;
      nextGuest = getNextGuest(index);
    } else {
      index = this.Guests.findIndex((y) => {
        return y.guestId == currentGuest?.guestId;
      });

      if (index > -1) {
        nextGuest = getNextGuest(index + 1);
      }
    }

    return nextGuest;
  }

  // True if there are services available for selection
  hasSelectableServices: boolean;
  areServicesLoaded: boolean;
  haveSeenServices: boolean;

  getService(serviceId: number): ReservationItemService | null {
    const s = this.Services.filter((u) => {
      return u.ServiceId == serviceId;
    });

    return s.length > 0 ? s[0] : null;
  }

  get totalServiceCost(): number {
    const totalDays = dateHelperUtils.calculateNumberOfNights(
      this.DateOfArrival,
      this.DateOfDeparture,
    );
    const totalGuests = this.NumberOfAdults + this.NumberOfChildren;
    const cost = rateHelperUtils.calcuateTotalServiceCost(
      this.Services,
      totalDays,
      totalGuests,
    );
    return cost;
  }

  toDTO(): PreCheckInReservationItemEditDTO {
    return {
      ReservationItemId: this.ReservationItemId,
      RoomConfigurationDropdownOptionId: this.RoomConfiguration?.Id,
      Guests: this.Guests.map((g) => {
        return g.toDTO();
      }),
      Services: this.Services.map((ris: ReservationItemService) => {
        return {
          ReservationItemId: ris.ReservationItemId,
          ServiceId: ris.ServiceId,
          Quantity: ris.Quantity,
          Service: ris.Service,
        } as PreCheckInReservationItemServiceDTO;
      }),
    } as PreCheckInReservationItemEditDTO;
  }
}

export class PreCheckInGuest implements PreCheckInGuestDTO {
  constructor(data: PreCheckInGuestDTO, config: PreCheckInConfigurationDTO) {
    Object.assign(this, data);

    this.haveReviewedGuest = false;
    this.guestId = utils.generateRandomId();

    this.config = config;
  }

  guestId: string;
  config: PreCheckInConfigurationDTO;

  IndividualId?: number;
  FirstName?: string;
  LastName?: string;
  Email?: string;
  Phone?: string;

  haveReviewedGuest: boolean;

  get isValid(): boolean {
    return (
      validationUtils.isValidStringValue(
        this.FirstName,
        this.config.FirstNameRequired,
      ) &&
      validationUtils.isValidStringValue(
        this.LastName,
        this.config.LastNameRequired,
      ) &&
      validationUtils.isValidStringValue(
        this.Email,
        this.config.EmailRequired,
      ) &&
      validationUtils.isValidStringValue(this.Phone, this.config.PhoneRequired)
    );
  }

  toDTO(): PreCheckInGuest {
    return {
      IndividualId: this.IndividualId,
      FirstName: this.FirstName,
      LastName: this.LastName,
      Email: this.Email,
      Phone: this.Phone,
    } as PreCheckInGuest;
  }
}

export class PreCheckInBooker {
  constructor(data: PreCheckInBookerDTO, config: PreCheckInConfigurationDTO) {
    Object.assign(this, data);
    this.config = config;
  }

  IndividualId?: number;
  FirstName?: string;
  LastName?: string;
  Email?: string;
  Phone?: string;
  Street?: string;
  City?: string;
  Country: ApiDropdown;
  StateOrCounty?: string;
  ZipCode?: string;

  config: PreCheckInConfigurationDTO;

  get fullname(): string {
    return this.FirstName + ' ' + this.LastName;
  }

  get areAddressDetailsValid(): boolean {
    return (
      validationUtils.isValidObjectValue(
        this.Country,
        this.config.CountryRequired,
      ) &&
      validationUtils.isValidStringValue(
        this.Street,
        this.config.StreetRequired,
      ) &&
      validationUtils.isValidStringValue(this.City, this.config.CityRequired) &&
      validationUtils.isValidStringValue(
        this.StateOrCounty,
        this.config.StateOrCountyRequired,
      ) &&
      validationUtils.isValidStringValue(
        this.ZipCode,
        this.config.PostCodeRequired,
      )
    );
  }

  get arePersonalDetailsValid(): boolean {
    return (
      validationUtils.isValidStringValue(
        this.FirstName,
        this.config.FirstNameRequired,
      ) &&
      validationUtils.isValidStringValue(
        this.LastName,
        this.config.LastNameRequired,
      ) &&
      validationUtils.isValidStringValue(
        this.Email,
        this.config.EmailRequired,
      ) &&
      validationUtils.isValidStringValue(this.Phone, this.config.PhoneRequired)
    );
  }

  get isValid(): boolean {
    return this.arePersonalDetailsValid && this.areAddressDetailsValid;
  }

  toDTO(): PreCheckInBookerDTO {
    return {
      FirstName: this.FirstName,
      LastName: this.LastName,
      Email: this.Email,
      Phone: this.Phone,
      Street: this.Street,
      City: this.City,
      CountryId: this.Country?.Id,
      StateOrCounty: this.StateOrCounty,
      ZipCode: this.ZipCode,
    } as PreCheckInBookerDTO;
  }
}
